import type { UIMessage } from "@ai-sdk/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import {
  DEFAULT_NEW_CONVERSATION_TITLE,
  extractFirstUserMessageTitle,
  FIRST_USER_TITLE_MAX_LEN,
} from "@/lib/utils/heraldChatUtils";
import useHeraldChatHistoryStore from "@/store/herald-chat/herald-chat-history.slice";

export interface UseHeraldChatSidebarSyncParams {
  conversationId: string | undefined;
  capturedConversationId: string | null;
  getTransportConversationId: () => string | null;
  getTransportConversationTitle: () => string | null;
  resetConversation: () => void;
  setConversationId: (id: string) => void;
  messages: UIMessage[];
  /** `useChat` status — drives allow-register and stream gating. */
  status: string;
  registerConversationFromFirstStream: (input: {
    conversationId: string;
    title?: string | null;
  }) => void;
  optimisticallyUpsertConversationAtTop: (input: {
    id: string;
    title?: string | null;
  }) => void;
}

/**
 * Syncs transport ↔ store for the sidebar: optimistic row, first-stream register, header title patch.
 */
export const useHeraldChatSidebarSync = ({
  conversationId,
  capturedConversationId,
  getTransportConversationId,
  getTransportConversationTitle,
  resetConversation,
  setConversationId,
  messages,
  status,
  registerConversationFromFirstStream,
  optimisticallyUpsertConversationAtTop,
}: UseHeraldChatSidebarSyncParams): void => {
  const navigate = useNavigate();
  const allPreviousConversations = useHeraldChatHistoryStore(
    (state) => state.allPreviousConversations,
  );
  const allowRegisterAfterFirstStreamRef = useRef(false);
  const hasRegisteredFromFirstStreamRef = useRef(false);
  const lastSidebarTitleKeyRef = useRef<string | null>(null);
  const postStreamTitleRefetchTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  /** Tracks route id across renders so we only clear transport when leaving a saved thread for `new`. */
  const prevConversationIdForTransportResetRef = useRef(conversationId);
  const sidebarTitlePreviewRef = useRef("");

  /** First user line only; value is reused while assistant chunks update `messages`. */
  const sidebarTitlePreview = useMemo(() => {
    const next = extractFirstUserMessageTitle(messages);
    if (next === sidebarTitlePreviewRef.current) {
      return sidebarTitlePreviewRef.current;
    }
    sidebarTitlePreviewRef.current = next;
    return next;
  }, [messages]);

  useEffect(() => {
    const prev = prevConversationIdForTransportResetRef.current;

    if (!conversationId) {
      lastSidebarTitleKeyRef.current = null;
      allowRegisterAfterFirstStreamRef.current = false;
      hasRegisteredFromFirstStreamRef.current = false;
      // Avoid reset on every `/chat/new` paint — that can clear `X-Conversation-Id` after it arrives but
      // before `replace: true` navigation, so the next register/navigate uses a stale id (old thread).
      if (prev !== undefined) {
        resetConversation();
      }
    }

    prevConversationIdForTransportResetRef.current = conversationId;
  }, [conversationId, resetConversation]);

  useEffect(() => {
    if (conversationId) {
      return;
    }
    if (status === "submitted" || status === "streaming") {
      allowRegisterAfterFirstStreamRef.current = true;
    }
  }, [conversationId, status]);

  useEffect(() => {
    if (conversationId) {
      setConversationId(conversationId);
    }
  }, [conversationId, setConversationId]);

  const transportConversationTitleSnapshot =
    getTransportConversationTitle() ?? "";

  const resolveSidebarTitle = (): string => {
    const fromHeader = getTransportConversationTitle()?.trim();
    if (fromHeader) {
      return fromHeader.slice(0, FIRST_USER_TITLE_MAX_LEN);
    }
    return sidebarTitlePreview || DEFAULT_NEW_CONVERSATION_TITLE;
  };

  const isStreaming = status === "streaming" || status === "submitted";

  useEffect(() => {
    const tid = getTransportConversationId();
    if (!tid) {
      lastSidebarTitleKeyRef.current = null;
      return;
    }
    if (conversationId != null && tid !== conversationId) {
      return;
    }

    const fromHeader = getTransportConversationTitle()?.trim();
    if (conversationId != null && !fromHeader) {
      const rowAlreadyInSidebar = allPreviousConversations.some(
        (conversation) => conversation.id === tid,
      );
      if (rowAlreadyInSidebar) {
        return;
      }
    }

    const title = resolveSidebarTitle();
    const key = `${tid}:${title}`;
    if (lastSidebarTitleKeyRef.current === key) {
      return;
    }
    lastSidebarTitleKeyRef.current = key;
    optimisticallyUpsertConversationAtTop({ id: tid, title });
  }, [
    allPreviousConversations,
    capturedConversationId, // Re-evaluate when transport captures a new conversation id.
    conversationId,
    getTransportConversationId,
    getTransportConversationTitle,
    optimisticallyUpsertConversationAtTop,
    sidebarTitlePreview,
    transportConversationTitleSnapshot,
  ]);

  useEffect(() => {
    const tid = getTransportConversationId();
    if (!tid || conversationId) {
      return;
    }
    if (!allowRegisterAfterFirstStreamRef.current) {
      return;
    }
    if (hasRegisteredFromFirstStreamRef.current) {
      return;
    }
    hasRegisteredFromFirstStreamRef.current = true;
    const resolved = resolveSidebarTitle();
    registerConversationFromFirstStream({
      conversationId: tid,
      title: resolved,
    });
    void navigate({
      replace: true,
      to: "/chat/$conversationId",
      params: { conversationId: tid },
    });
    // Defer one microtask so the router has committed the navigation before we refresh
    // the list (avoids merging with a stale route id). Not cancelled on unmount — harmless
    // if the pane unmounts first; the store action is idempotent enough for that edge case.
    // Header title is often invisible to JS (CORS); BE title lands on GetConversations.
    queueMicrotask(() => {
      void useHeraldChatHistoryStore.getState().fetchAllPreviousConversations({
        requestPolicy: "network-only",
        activeConversationId: tid,
      });
    });
  }, [
    capturedConversationId, // Re-evaluate when transport captures a new conversation id.
    conversationId,
    getTransportConversationId,
    getTransportConversationTitle,
    navigate,
    registerConversationFromFirstStream,
    sidebarTitlePreview,
    transportConversationTitleSnapshot,
  ]);

  useEffect(() => {
    if (!transportConversationTitleSnapshot) {
      return;
    }
    const tid = getTransportConversationId();
    if (!tid) {
      return;
    }
    if (conversationId != null && tid !== conversationId) {
      return;
    }
    void useHeraldChatHistoryStore.getState().fetchAllPreviousConversations({
      requestPolicy: "network-only",
      activeConversationId: conversationId ?? tid,
    });
  }, [
    capturedConversationId,
    conversationId,
    getTransportConversationId,
    transportConversationTitleSnapshot,
  ]);

  useEffect(() => {
    if (isStreaming) {
      if (postStreamTitleRefetchTimeoutRef.current != null) {
        clearTimeout(postStreamTitleRefetchTimeoutRef.current);
        postStreamTitleRefetchTimeoutRef.current = undefined;
      }
      return;
    }
    const tid = getTransportConversationId();
    const activeId = conversationId ?? tid;
    if (!activeId) {
      return;
    }

    postStreamTitleRefetchTimeoutRef.current = setTimeout(() => {
      void useHeraldChatHistoryStore.getState().fetchAllPreviousConversations({
        requestPolicy: "network-only",
        activeConversationId: activeId,
      });
      postStreamTitleRefetchTimeoutRef.current = undefined;
    }, 2000);

    return () => {
      if (postStreamTitleRefetchTimeoutRef.current != null) {
        clearTimeout(postStreamTitleRefetchTimeoutRef.current);
        postStreamTitleRefetchTimeoutRef.current = undefined;
      }
    };
  }, [
    capturedConversationId,
    conversationId,
    getTransportConversationId,
    isStreaming,
  ]);
};
